import React, { useMemo } from "react";

import { css } from "@emotion/css";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { BGMData } from "@/models/BGMLogbook.ts";
import { getFoodMealTranslation } from "@/models/diabetes/FoodModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { BackToTop } from "@/uiKit/molecules/BackToTop.tsx";

import { BGMTableBody } from "./TableBody/BGMTableBody";
import { PrettyComment } from "./TableBody/Comments/PrettyComment";
import { GlycemiaBadges } from "./TableBody/GlycemiaBadges";
import { Insulin } from "./TableBody/Insulin";
import { PrettyDate } from "./TableBody/PrettyDate";
import { BGMTableHeader } from "./TableHeader/BGMTableHeader";
import { HeaderWithTooltip } from "./TableHeader/HeaderWithTooltip";

const columnHelper = createColumnHelper<BGMData>();
const duplicateHeader = (
  patientId: string,
  groupHeader: "breakfast" | "lunch" | "dinner",
  className: string,
  t: TFunction<"tl", undefined>,
) => {
  return columnHelper.group({
    id: groupHeader,
    header: () => (
      <Typography variant="body" className={className}>
        {t(getFoodMealTranslation(groupHeader))}
      </Typography>
    ),
    columns: [
      columnHelper.accessor(`${groupHeader}.bloodSugarBefore`, {
        header: () => (
          <HeaderWithTooltip
            id={`bgm-header-${groupHeader}-glycemia-before`}
            tooltipContent={t("bgmLogBook.glycemiaBefore")}
            title={t("bgmLogBook.glycemiaBeforeHeader")}
          />
        ),
        cell: info => (
          <GlycemiaBadges patientId={patientId} data={info.getValue()} meal={groupHeader} timing="before" />
        ),
        size: 50,
      }),
      columnHelper.accessor(`${groupHeader}.fastInsulin`, {
        header: () => (
          <HeaderWithTooltip
            id={`bgm-header-${groupHeader}-fast-insulin`}
            tooltipContent={t("bgmLogBook.fastInsulin")}
            title={t("bgmLogBook.fastInsulinHeader")}
          />
        ),
        cell: info => <Insulin data={info.getValue()} />,
        size: 50,
      }),
      columnHelper.accessor(`${groupHeader}.bloodSugarAfter`, {
        header: () => (
          <HeaderWithTooltip
            id={`bgm-header-${groupHeader}-glycemia-after`}
            tooltipContent={t("bgmLogBook.glycemiaAfter")}
            title={t("bgmLogBook.glycemiaAfterHeader")}
          />
        ),
        cell: info => <GlycemiaBadges patientId={patientId} data={info.getValue()} meal={groupHeader} timing="after" />,
        size: 50,
      }),
      columnHelper.accessor(`${groupHeader}.slowInsulin`, {
        header: () => (
          <HeaderWithTooltip
            id={`bgm-header-${groupHeader}-slow-insulin`}
            tooltipContent={t("bgmLogBook.slowInsulin")}
            title={t("bgmLogBook.slowInsulinHeader")}
          />
        ),
        cell: info => <Insulin data={info.getValue()} />,
        size: 50,
      }),
    ],
  });
};

type BGMContainerBodyProps = {
  patientId: string;
  data: BGMData[];
  loadTop?: () => void;
  loadTopText?: string;
  loadBottom?: () => void;
  loadBottomText?: string;
};

export const BGMContainerBody: React.FC<BGMContainerBodyProps> = ({
  patientId,
  data,
  loadTop,
  loadTopText,
  loadBottom,
  loadBottomText,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor("date", {
        header: () => (
          <Typography variant="body" className={styles.headerItem}>
            {t("bgmLogBook.date")}
          </Typography>
        ),
        cell: info => <PrettyDate date={info.getValue()} />,
        size: 90,
      }),
      duplicateHeader(patientId, "breakfast", styles.headerItem, t),
      duplicateHeader(patientId, "lunch", styles.headerItem, t),
      duplicateHeader(patientId, "dinner", styles.headerItem, t),
      columnHelper.group({
        id: "night",
        header: () => (
          <Typography variant="body" className={styles.headerItem}>
            {t("bgmLogBook.nightTime")}
          </Typography>
        ),
        columns: [
          columnHelper.accessor("night.bloodSugar", {
            header: () => (
              <Typography variant="body" className={styles.headerItem}>
                {t("bgmLogBook.glycemia")}
              </Typography>
            ),
            cell: info => <GlycemiaBadges patientId={patientId} data={info.getValue()} meal="lunch" timing="after" />,
            size: 100,
          }),
          columnHelper.accessor("night.slowInsulin", {
            header: () => (
              <HeaderWithTooltip
                id="bgm-header-night-slow-insulin"
                tooltipContent={t("bgmLogBook.slowInsulin")}
                title={t("bgmLogBook.slowInsulinHeader")}
              />
            ),
            cell: info => <Insulin data={info.getValue()} />,
            size: 50,
          }),
        ],
      }),
      columnHelper.accessor("comments", {
        header: () => (
          <Typography variant="body" className={styles.headerItem}>
            {t("bgmLogBook.comment")}
          </Typography>
        ),
        cell: info => <PrettyComment comment={info.getValue()} />,
        size: 300,
      }),
    ],
    [styles.headerItem, t, patientId],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <>
      <TableContainer className={styles.container}>
        <BackToTop id="bgm-logbook-back-to-top" />
        <Table aria-label="a dense table" className={styles.table} size="small" stickyHeader>
          <BGMTableHeader table={table} className={styles.header} />
          <BGMTableBody
            table={table}
            loadTop={loadTop}
            loadTopText={loadTopText}
            loadBottom={loadBottom}
            loadBottomText={loadBottomText}
          />
        </Table>
      </TableContainer>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.spacing(6)};
    overflow-y: clip;
    overflow-x: auto;
  `,
  scrollBar: css`
    height: 40vh;
  `,
  table: css`
    width: 100%;
    overflow: clip;
  `,
  header: css`
    top: 0;
    position: sticky;
  `,
  headerItem: css`
    color: ${theme.palette.violet.main};
  `,
});
